import type { PlacedObject } from '../types';

export const MAX_OBJECT_HIERARCHY_DEPTH = 3;

export function getObjectDepth(objects: PlacedObject[], id: string) {
  const objectById = new Map(objects.map((object) => [object.id, object]));
  const visited = new Set<string>();
  let current = objectById.get(id);
  let depth = 0;

  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    depth += 1;
    current = current.supportId ? objectById.get(current.supportId) : undefined;
  }

  return depth;
}

export function getObjectSubtreeHeight(objects: PlacedObject[], id: string) {
  const childrenBySupport = new Map<string, PlacedObject[]>();
  objects.forEach((object) => {
    if (!object.supportId) return;
    const children = childrenBySupport.get(object.supportId) ?? [];
    children.push(object);
    childrenBySupport.set(object.supportId, children);
  });

  const measure = (objectId: string, visited: Set<string>): number => {
    if (visited.has(objectId)) return 0;
    const nextVisited = new Set(visited).add(objectId);
    const childHeights = (childrenBySupport.get(objectId) ?? []).map((child) => measure(child.id, nextVisited));
    return 1 + Math.max(0, ...childHeights);
  };

  return measure(id, new Set());
}

export function canStackObject(objects: PlacedObject[], id: string, supportId: string) {
  const objectById = new Map(objects.map((object) => [object.id, object]));
  const source = objectById.get(id);
  const support = objectById.get(supportId);
  if (!source || !support || id === supportId || source.supportId === supportId) return false;

  const visited = new Set<string>();
  let current: PlacedObject | undefined = support;
  while (current && !visited.has(current.id)) {
    if (current.id === id) return false;
    visited.add(current.id);
    current = current.supportId ? objectById.get(current.supportId) : undefined;
  }

  return getObjectDepth(objects, supportId) + getObjectSubtreeHeight(objects, id) <= MAX_OBJECT_HIERARCHY_DEPTH;
}

export function canAddObjectOnTop(objects: PlacedObject[], supportId: string) {
  return getObjectDepth(objects, supportId) < MAX_OBJECT_HIERARCHY_DEPTH;
}
